import {firestore} from 'firebase/app'
import {FirestoreQuery} from '../Firestore/FirestoreQuery'
import {PriceList} from './PriceList'
import {PriceListMapper} from './PriceListMapper'
import {CompanyTypeEnum} from '..'
import {PriceListEntry} from './PriceListEntry/PriceListEntry'
import {v4 as uuid} from 'uuid'

export class PriceListCollection {
  protected collection: string = 'priceList'

  constructor(protected db: firestore.Firestore, protected batch?: firestore.WriteBatch) {}

  watchPriceListsByCompanyId(companyId: string, companyType: CompanyTypeEnum): PriceList[] {
    const query = this.initQuery()
    query.setQuery(this.getPriceListsByCompanyIdQuery(query, companyId, companyType))
    return query.watchList()
  }

  watchPriceListsByRestaurantAndVendor(
    companyId: string,
    vendorId: string,
    cb?: (priceLists: PriceList[], unsub?: () => void) => void
  ): PriceList[] {
    const query = this.initQuery()
    query.setQuery(this.getActivePriceListsByRestaurantAndVendorQuery(query, companyId, vendorId))
    return query.watchList(cb)
  }

  list(): Promise<PriceList[]> {
    const query = this.initQuery()
    return query.list()
  }

  listPriceListsByCompanyId(companyId: string, companyType: CompanyTypeEnum, active?: boolean): Promise<PriceList[]> {
    const query = this.initQuery()
    active
      ? query.setQuery(this.getActivePriceListsByCompanyIdQuery(query, companyId, companyType))
      : query.setQuery(this.getPriceListsByCompanyIdQuery(query, companyId, companyType))
    return query.list()
  }

  listPriceListsByRepId(repId: string): Promise<PriceList[]> {
    const query = this.initQuery()
    query.setQuery(this.getPriceListsByRepIdQuery(query, repId))
    return query.list()
  }

  listPriceListsByRestaurantAndVendor(
    restaurantCompanyId: string,
    vendorCompanyId: string,
    active?: boolean
  ): Promise<PriceList[]> {
    const query = this.initQuery()
    active
      ? query.setQuery(this.getActivePriceListsByRestaurantAndVendorQuery(query, restaurantCompanyId, vendorCompanyId))
      : query.setQuery(this.getPriceListsByRestaurantAndVendorQuery(query, restaurantCompanyId, vendorCompanyId))
    return query.list()
  }

  watch(id: string): { data: PriceList | null, unsub: () => void } {
    const query = this.initQuery()
    return query.watch(id)
  }

  async findPriceListByRestaurantAndVendorAndPhoneNumber(companyId: string, vendorId: string, phoneNumber: string): Promise<PriceList | null> {
    const query = this.initQuery()
    query.setQuery(
        query.collection(this.collection)
            .where('restaurantCompanyId', '==', companyId)
            .where('vendorCompanyId', '==', vendorId)
            .where('vendorRepPhoneNumber', '==', phoneNumber)
            .limit(1)
    )

    return ( await query.list() )[0]
  }

  get(id: string): Promise<PriceList | null> {
    const query = this.initQuery()
    return query.find(id)
  }

  save(priceList: PriceList): Promise<PriceList> {
    // dedup entries
    const dedupedEntries = new Map<string, PriceListEntry>();
    priceList.entries.forEach(entry => {
      entry.description = entry.description.trim()
      const key = entry.description + entry.upc;
      const existingEntry = dedupedEntries.get(key);
      if (!existingEntry) {
        dedupedEntries.set(key, entry);
      }

      if(!entry.upc || entry.upc === '') {
        entry.upc = uuid()
      }
    })
    priceList.entries = [...dedupedEntries.values()];
    priceList.vendorCompanyName = (priceList.vendorCompanyName || '').trim()
    priceList.vendorRepName = (priceList.vendorRepName || '').trim()

    const query = this.initQuery()
    return query.save(priceList)
  }

  async delete(priceList: PriceList) {
    const query = this.initQuery();
    return query.delete(priceList)
  }

  protected initQuery(): FirestoreQuery<PriceList> {
    const query = new FirestoreQuery<PriceList>(this.db, new PriceListMapper())
    query.collection(this.collection)

    return query
  }

  protected getPriceListsByCompanyIdQuery(query: FirestoreQuery<PriceList>, companyId: string, companyType: CompanyTypeEnum) {
    const field = companyType === CompanyTypeEnum.VENDOR ? 'vendorCompanyId' : 'restaurantCompanyId'
    return query.collection(this.collection).where(field, '==', companyId)
  }

  protected getActivePriceListsByRestaurantAndVendorQuery(query: FirestoreQuery<PriceList>, companyId: string, vendorId: string) {
    return query.collection(this.collection)
      .where('restaurantCompanyId', '==', companyId)
      .where('vendorCompanyId', '==', vendorId)
      .where('active', '==', true)
  }

  protected getPriceListsByRestaurantAndVendorQuery(query: FirestoreQuery<PriceList>, companyId: string, vendorId: string) {
    return query.collection(this.collection)
      .where('restaurantCompanyId', '==', companyId)
      .where('vendorCompanyId', '==', vendorId)
  }

  protected getActivePriceListsByCompanyIdQuery(query: FirestoreQuery<PriceList>, companyId: string, companyType: CompanyTypeEnum) {
    const field = companyType === CompanyTypeEnum.VENDOR ? 'vendorCompanyId' : 'restaurantCompanyId'
    return query.collection(this.collection)
      .where(field, '==', companyId)
      .where('active', '==', true)
  }

  protected getPriceListsByRepIdQuery(query: FirestoreQuery<PriceList>, repId: string) {
    return query.collection(this.collection).where('vendorRepId', '==', repId)
  }
}
